<script setup lang="ts">
import type { SearchDatepickerProperties, SearchLegRequest } from '@optionway/models';
import type { DateRange } from 'reka-ui';
import type { ComponentPublicInstance } from 'vue';
import type { LabelledFieldMode } from '~/components/ui-custom/labelled-field/models';
import { AlertCircle, Calendar } from 'lucide-vue-next';
import { breakpointsTailwind } from '@vueuse/core';

const prefix = 'components.search.datepicker';

const properties = withDefaults(
  defineProps<
    SearchDatepickerProperties & {
      departureError?: string;
      returnError?: string;
      compact?: boolean;
      inBar?: boolean;
      oneWay?: boolean;
      labelMode?: LabelledFieldMode;
      forceFocus?: number;
      origin?: string;
      destination?: string;
    }
  >(),
  // Default to undefined (not Vue's Boolean-cast false) so `oneWay ?? store` falls back to the store.
  {
    departureError: undefined,
    returnError: undefined,
    oneWay: undefined,
    labelMode: undefined,
    forceFocus: undefined,
    origin: undefined,
    destination: undefined,
    showDirectFlights: undefined,
  },
);

const departureDate = defineModel<SearchLegRequest['departureDate']>('departure-date');
const returnDate = defineModel<SearchLegRequest['departureDate']>('return-date');
const autoOpen = defineModel<boolean>('autoOpen', { default: false });

const emit = defineEmits<{ 'range-selected': [] }>();

const searchCriteria = useSearchCriteriaStore();
const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.components?.searchDatepicker;
const apiUrl = $appConfig.api?.url;
const isMounted = useMounted();

const breakpoints = useBreakpoints(breakpointsTailwind);
// lg, like the range calendar: from there it renders the desktop copy of the triggers, below it the mobile one.
const isDesktop = breakpoints.greaterOrEqual('lg');

const isOneWay = computed<boolean>(() => properties.oneWay ?? searchCriteria.isOneWay);

const componentConfig = computed<SearchDatepickerProperties>(() => ({
  showDirectFlights: properties.showDirectFlights ?? config?.showDirectFlights ?? false,
  minDaysBeforeDeparture: properties.minDaysBeforeDeparture ?? config?.minDaysBeforeDeparture,
}));
const { data: directFlightDates } = useQuery(() =>
  useDirectFlightDatesOptions({
    enabled: isMounted.value && !!componentConfig.value.showDirectFlights,
    fromCity: properties.origin ?? '',
    toCity: properties.destination ?? '',
    apiUrl,
  }),
);
const { data: returnDirectFlightDates } = useQuery(() =>
  useDirectFlightDatesOptions({
    enabled: isMounted.value && !!componentConfig.value.showDirectFlights && !isOneWay.value,
    fromCity: properties.destination ?? '',
    toCity: properties.origin ?? '',
    apiUrl,
  }),
);

const inputId = useId();

// The start slot renders once per layout, so one template ref would hold whichever copy mounted last.
const departureTriggers = new Map<string, ComponentPublicInstance | null>();

const departureDateValue = computed(() => (departureDate.value ? toDateValue(departureDate.value) : undefined));
const returnDateValue = computed(() => (returnDate.value ? toDateValue(returnDate.value) : undefined));

const calendarRange = computed<DateRange>({
  get: () => ({
    start: departureDateValue.value,
    end: returnDateValue.value,
  }),
  set: (v) => {
    departureDate.value = v.start?.toString();
    returnDate.value = v.end?.toString();
  },
});

watch(
  () => properties.forceFocus,
  (request) => {
    if (request === undefined) {
      return;
    }
    unrefElement(departureTriggers.get(isDesktop.value ? 'desktop' : 'mobile'))?.focus();
  },
);

const setDepartureTrigger = (layout: string, trigger: ComponentPublicInstance | Element | null) => {
  // The ref sits on UiButton, so Vue hands over its component instance, or null on unmount.
  if (trigger instanceof Element) {
    return;
  }
  departureTriggers.set(layout, trigger);
};
</script>

<template>
  <div class="flex flex-col gap-1">
    <UiCustomRangeCalendar
      v-model="calendarRange"
      v-model:auto-open="autoOpen"
      :one-way="isOneWay"
      :min-date="properties.minDate"
      :min-days-before-departure="componentConfig.minDaysBeforeDeparture"
      :direct-flight-dates="directFlightDates?.uniqueDates"
      :return-direct-flight-dates="returnDirectFlightDates?.uniqueDates"
      @range-selected="emit('range-selected')"
    >
      <template #start="{ formatted, isOpen, layout }">
        <UiField
          class="gap-1"
          :data-invalid="!!properties.departureError"
        >
          <UiCustomLabelledField
            :key="isOneWay ? 'atDate' : 'departureDate'"
            v-slot="{ labelId }"
            :label="$t(`${prefix}.${isOneWay ? 'atDate' : 'departureDate'}`)"
            :for="`dateInput-${inputId}-${layout}`"
            :mode="properties.compact ? 'none' : properties.labelMode"
            :floated="!!formatted || isOpen"
          >
            <UiButton
              :id="`dateInput-${inputId}-${layout}`"
              :ref="(trigger) => setDepartureTrigger(layout, trigger)"
              :aria-labelledby="`${labelId} dateInput-${inputId}-${layout}`"
              :aria-invalid="!!properties.departureError"
              :aria-describedby="properties.departureError ? `dateInput-${inputId}-error` : undefined"
              variant="secondary"
              color="gray"
              :class="
                cn(
                  'flex w-full items-center justify-start gap-2 whitespace-nowrap text-left font-normal shadow-sm',
                  properties.inBar
                    ? 'rounded-none border-0 shadow-none hover:bg-gray-50 hover:shadow-none focus-visible:bg-gray-50 focus-visible:ring-0'
                    : 'rounded-lg border-gray-500 bg-surface hover:bg-surface focus-visible:ring-2 focus-visible:ring-brand-primary-500 focus-visible:ring-offset-0',
                  properties.compact ? 'h-9 px-3 text-sm [&_svg]:size-4' : 'h-11 px-3.5 text-base [&_svg]:size-5',
                  isOpen && !properties.inBar && 'ring-2 ring-brand-primary-500',
                  isOpen && properties.inBar && 'bg-gray-50',
                  properties.departureError && !properties.inBar && 'ring-2 ring-inset ring-red-700',
                )
              "
            >
              <Calendar class="shrink-0 text-gray-500" />
              <Transition
                mode="out-in"
                enter-active-class="transition-opacity duration-150"
                leave-active-class="transition-opacity duration-150"
                enter-from-class="opacity-0"
                leave-from-class="opacity-100"
                enter-to-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <span
                  :key="formatted ?? `placeholder-${isOneWay ? 'atDate' : 'departureDate'}`"
                  :class="formatted ? 'text-text-primary-900' : 'text-gray-500 xl:hidden'"
                >
                  {{ formatted ?? '' }}
                </span>
              </Transition>
              <AlertCircle
                v-if="properties.departureError && !properties.inBar"
                :class="[properties.compact ? 'size-4' : 'size-5', 'ml-auto shrink-0 text-red-700']"
              />
            </UiButton>
          </UiCustomLabelledField>
        </UiField>
      </template>

      <template
        v-if="!isOneWay"
        #end="{ formatted, isOpen, layout }"
      >
        <UiField
          class="gap-1"
          :data-invalid="!!properties.returnError"
        >
          <UiCustomLabelledField
            v-slot="{ labelId }"
            :label="$t(`${prefix}.returnDate`)"
            :for="`dateInput-${inputId}-end-${layout}`"
            :mode="properties.compact ? 'none' : properties.labelMode"
            :floated="!!formatted || isOpen"
          >
            <UiButton
              :id="`dateInput-${inputId}-end-${layout}`"
              :aria-labelledby="`${labelId} dateInput-${inputId}-end-${layout}`"
              :aria-invalid="!!properties.returnError"
              :aria-describedby="properties.returnError ? `dateInput-${inputId}-end-error` : undefined"
              variant="secondary"
              color="gray"
              :class="
                cn(
                  'flex w-full items-center justify-start gap-2 whitespace-nowrap text-left font-normal shadow-sm',
                  properties.inBar
                    ? 'rounded-none border-0 shadow-none hover:bg-gray-50 hover:shadow-none focus-visible:bg-gray-50 focus-visible:ring-0'
                    : 'rounded-lg border-gray-500 bg-surface hover:bg-surface focus-visible:ring-2 focus-visible:ring-brand-primary-500 focus-visible:ring-offset-0',
                  properties.compact ? 'h-9 px-3 text-sm [&_svg]:size-4' : 'h-11 px-3.5 text-base [&_svg]:size-5',
                  isOpen && !properties.inBar && 'ring-2 ring-brand-primary-500',
                  isOpen && properties.inBar && 'bg-gray-50',
                  properties.returnError && !properties.inBar && 'ring-2 ring-inset ring-red-700',
                )
              "
            >
              <Calendar class="shrink-0 text-gray-500" />
              <Transition
                mode="out-in"
                enter-active-class="transition-opacity duration-150"
                leave-active-class="transition-opacity duration-150"
                enter-from-class="opacity-0"
                leave-from-class="opacity-100"
                enter-to-class="opacity-100"
                leave-to-class="opacity-0"
              >
                <span
                  :key="formatted ?? 'placeholder-returnDate'"
                  :class="formatted ? 'text-text-primary-900' : 'text-gray-500 xl:hidden'"
                >
                  {{ formatted ?? '' }}
                </span>
              </Transition>
              <AlertCircle
                v-if="properties.returnError && !properties.inBar"
                :class="[properties.compact ? 'size-4' : 'size-5', 'ml-auto shrink-0 text-red-700']"
              />
            </UiButton>
          </UiCustomLabelledField>
        </UiField>
      </template>
    </UiCustomRangeCalendar>

    <!-- empty:hidden: without an error the row only holds the Transitions' v-if comments, which :empty ignores,
         so the row drops out of the column and its gap with it -->
    <div class="flex gap-2 empty:hidden">
      <Transition
        enter-active-class="transition-all duration-200 ease-out motion-reduce:transition-none"
        enter-from-class="opacity-0 -translate-y-1"
        leave-active-class="transition-all duration-150 ease-in motion-reduce:transition-none"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <UiFieldError
          v-if="properties.departureError"
          :id="`dateInput-${inputId}-error`"
          class="flex-1 text-xs text-red-700"
        >
          {{ properties.departureError }}
        </UiFieldError>
      </Transition>
      <Transition
        v-if="!isOneWay"
        enter-active-class="transition-all duration-200 ease-out motion-reduce:transition-none"
        enter-from-class="opacity-0 -translate-y-1"
        leave-active-class="transition-all duration-150 ease-in motion-reduce:transition-none"
        leave-to-class="opacity-0 -translate-y-1"
      >
        <UiFieldError
          v-if="properties.returnError"
          :id="`dateInput-${inputId}-end-error`"
          class="flex-1 text-xs text-red-700"
        >
          {{ properties.returnError }}
        </UiFieldError>
      </Transition>
    </div>
  </div>
</template>
